import { createElement, useMemo, type FC } from 'react';

import { useCms } from './provider';
import { RenderingContext } from './rendering-context';

const APP_PLACEHOLDER_PREFIX = 'jss-app-';
const APP_PLACEHOLDER_REGEX = /^jss-app-(?<application>.+)/;

/** The single place the `jss-app-*` convention is matched. Everything below reads from this. */
const applicationPlaceholderNames = (placeholders: Record<string, unknown> | undefined): string[] =>
  Object.keys(placeholders ?? {}).filter(name => APP_PLACEHOLDER_REGEX.test(name));

/**
 * Whether the page mounts an application at all.
 *
 * The non-throwing counterpart of `getApplicationPlaceholderName`, which answers
 * the same question by exception: right inside `AppPlaceholder`, where its absence
 * is a bug, and wrong for a layout deciding which branch to take.
 *
 * This is the exported form of the question on purpose. Publishing the regex
 * instead invites every caller to re-implement the check.
 */
export const hasApplicationPlaceholder = (placeholders: Record<string, unknown> | undefined): boolean =>
  applicationPlaceholderNames(placeholders).length > 0;

/**
 * Find the application-mounting placeholder name on the current rendering.
 * Mirrors `getPlaceholderNameFromRendering` from `@eneco/sitecore-common`.
 */
export const getApplicationPlaceholderName = (placeholders: Record<string, unknown> | undefined): string => {
  const [found] = applicationPlaceholderNames(placeholders);
  if (!found) {
    throw new Error(`No placeholder found matching ${APP_PLACEHOLDER_REGEX}`);
  }
  return found;
};

/**
 * Returns a `{ [componentName]: rendering }` map of the current page's
 * application-placeholder children. Used by app code that wants to read
 * sibling renderings without rendering them.
 */
export const usePlaceholderContent = <T extends Record<string, unknown>>(): T => {
  const { page } = useCms();
  const placeholderName = getApplicationPlaceholderName(page.root.placeholders);
  const children = page.root.placeholders?.[placeholderName] ?? [];
  return children.reduce((acc, rendering) => ({ ...acc, [rendering.componentName]: rendering }), {} as T);
};

/**
 * Renders the application component for the current route's
 * `jss-app-*` placeholder. Equivalent of `AppPlaceholder` in
 * `@eneco/sitecore-common`.
 */
export const AppPlaceholder: FC = () => {
  const { page, components } = useCms();
  const placeholderName = getApplicationPlaceholderName(page.root.placeholders);
  const componentName = placeholderName.replace(APP_PLACEHOLDER_PREFIX, '');
  const Component = components[componentName];
  // Provide a synthetic rendering so `useContent<T>()` works inside the app.
  const synthetic = useMemo(() => ({ componentName, content: {}, uid: placeholderName }), [componentName, placeholderName]);
  if (!Component) return null;
  return <RenderingContext.Provider value={synthetic}>{createElement(Component)}</RenderingContext.Provider>;
};
